// Tiny chiptune sound effects made with the Web Audio API, so there are no audio files to load.

export type SoundName = 'click' | 'flip' | 'roll' | 'win' | 'lose' | 'cashout' | 'boom' | 'gem' | 'launch'

let context: AudioContext | null = null
let enabled = true

type ToneOptions = {
    type?: OscillatorType
    volume?: number
    delay?: number
    slide?: number
}

export function setSoundsEnabled(value: boolean): void {
    enabled = value
}

function audio(): AudioContext | null {
    if (typeof window === 'undefined') return null

    if (!context) {
        const Constructor = window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext

        if (!Constructor) return null

        context = new Constructor()
    }

    if (context.state === 'suspended') void context.resume()

    return context
}

function tone(frequency: number, duration: number, { type = 'square', volume = 0.04, delay = 0, slide }: ToneOptions = {}): void {
    const ctx = audio()

    if (!ctx) return

    const start = ctx.currentTime + delay
    const oscillator = ctx.createOscillator()
    const gain = ctx.createGain()

    oscillator.type = type
    oscillator.frequency.setValueAtTime(frequency, start)

    if (slide) oscillator.frequency.exponentialRampToValueAtTime(slide, start + duration)

    gain.gain.setValueAtTime(volume, start)
    gain.gain.exponentialRampToValueAtTime(0.0001, start + duration)
    oscillator.connect(gain)
    gain.connect(ctx.destination)
    oscillator.start(start)
    oscillator.stop(start + duration + 0.03)
}

function noise(duration: number, volume: number): void {
    const ctx = audio()

    if (!ctx) return

    const buffer = ctx.createBuffer(1, Math.floor(ctx.sampleRate * duration), ctx.sampleRate)
    const data = buffer.getChannelData(0)

    for (let i = 0; i < data.length; i++) data[i] = (Math.random() * 2 - 1) * (1 - i / data.length)

    const source = ctx.createBufferSource()
    const gain = ctx.createGain()

    source.buffer = buffer
    gain.gain.setValueAtTime(volume, ctx.currentTime)
    gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + duration)
    source.connect(gain)
    gain.connect(ctx.destination)
    source.start()
}

export function play(name: SoundName): void {
    if (!enabled) return

    try {
        switch (name) {
            case 'click':
                tone(660, 0.05, { volume: 0.025 })
                break
            case 'flip':
                for (let i = 0; i < 6; i++) tone(480 + i * 70, 0.05, { delay: i * 0.09, volume: 0.025 })
                break
            case 'roll':
                for (let i = 0; i < 5; i++) tone(300 + Math.random() * 400, 0.04, { delay: i * 0.07, volume: 0.025 })
                break
            case 'win':
                tone(523, 0.12)
                tone(659, 0.12, { delay: 0.1 })
                tone(784, 0.22, { delay: 0.2 })
                break
            case 'lose':
                tone(260, 0.3, { type: 'sawtooth', slide: 110, volume: 0.03 })
                break
            case 'cashout':
                tone(784, 0.1)
                tone(1046, 0.2, { delay: 0.09 })
                break
            case 'boom':
                noise(0.6, 0.25)
                tone(110, 0.45, { type: 'sawtooth', slide: 35, volume: 0.05 })
                break
            case 'gem':
                tone(988, 0.08, { type: 'triangle', volume: 0.05 })
                tone(1318, 0.14, { type: 'triangle', delay: 0.06, volume: 0.05 })
                break
            case 'launch':
                tone(180, 0.5, { type: 'sawtooth', slide: 520, volume: 0.02 })
                break
        }
    }
    catch {
        // Sound is a nice-to-have; never let it break a game.
    }
}
